import {
  Box,
  Grid,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { useCallback, type FunctionComponent } from "react";
import { Handle, Position } from "reactflow";
import type NodeData from "../features/NodeData";
import Katex from "../latex/Katex";
import { monoFontFamily } from "../theme";

interface InputItemProps {
  id: string;
  data: NodeData;
  itemHeight: number;
  inputWidth: number;
  // If we don't offset the handle, the handle would align its left edge with
  // the left edge of the input label
  handleLeftOffset: string;
  handleSize: number;
  handleHoverScale: number;
  handleColor: string;
  handleHoverColor: string;
  handleBorderColor: string;
  onInputChange: (inputPortId: string, value: string) => void;
}

const InputItems: FunctionComponent<InputItemProps> = ({
  id,
  data,
  itemHeight,
  inputWidth,
  handleLeftOffset,
  handleSize,
  handleHoverScale,
  handleColor,
  handleHoverColor,
  handleBorderColor,
  onInputChange,
}) => {
  const getInputId = useCallback(
    (portId: string): string => {
      return `input-item-${id}-${portId}`;
    },
    [id],
  );

  // Centers the dot on the left border of the node body, which sits one body
  // padding to the left of the input row
  const getInputHandleLeft = useCallback((): string => {
    return `calc(${handleLeftOffset} - ${handleSize / 2}px)`;
  }, [handleLeftOffset, handleSize]);

  return (
    <Grid container columnSpacing={1} wrap="nowrap">
      {/* Handles and labels */}
      <Grid item xs>
        {data.inputItems.map((item) => (
          <Box
            key={item.id}
            display="flex"
            justifyContent="left"
            alignItems="center"
            position="relative" // for Handle
            height={itemHeight}
          >
            {/* Handle */}
            {item.showHandle && (
              <Box
                sx={{
                  "& .react-flow__handle": {
                    transition:
                      "transform 120ms ease-out, background-color 120ms ease-out",
                  },
                  "& .react-flow__handle:hover": {
                    backgroundColor: `${handleHoverColor} !important`,
                    // Keeps the dot centered on its row while it grows
                    transform: `translateY(-50%) scale(${handleHoverScale})`,
                  },
                }}
              >
                <Handle
                  id={item.id}
                  position={Position.Left}
                  style={{
                    position: "absolute", // needs position="relative" on parent
                    background: handleColor,
                    // The ring separates the dot from whatever is behind it,
                    // which is the node body on one side and the canvas on the
                    // other
                    border: `2px solid ${handleBorderColor}`,
                    borderRadius: "50%",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                    // Lines the dot up with the middle of its own input row
                    top: "50%",
                    left: getInputHandleLeft(),
                    transform: "translateY(-50%)",
                    width: handleSize,
                    height: handleSize,
                  }}
                  type="target"
                />
              </Box>
            )}

            {/* Label */}
            {!item.showHandle &&
              (item.showInputField ? (
                <InputLabel htmlFor={getInputId(item.id)}>
                  <Katex latex={item.label} />
                </InputLabel>
              ) : (
                // Don't use <input> because otherwise the `htmlFor` is invalid
                <Typography color="text.secondary">
                  <Katex latex={item.label} />
                </Typography>
              ))}
          </Box>
        ))}
      </Grid>

      {/* Inputs */}
      <Grid item xs>
        {data.inputItems.map((item) => (
          <Box
            key={item.id}
            display="flex"
            justifyContent="right"
            alignItems="center"
            height={itemHeight}
          >
            {/* Input */}
            {item.showInputField && (
              <OutlinedInput
                id={getInputId(item.id)}
                data-testid={getInputId(item.id)}
                value={item.value}
                size="small"
                inputProps={{
                  style: {
                    padding: "4px 8px",
                    textAlign: "right",
                    width: inputWidth,
                    fontFamily: monoFontFamily,
                  },
                }}
                onChange={(event) => {
                  onInputChange(item.id, event.target.value);
                }}
              ></OutlinedInput>
            )}
          </Box>
        ))}
      </Grid>
    </Grid>
  );
};

export default InputItems;
